import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, InputNumber, message, Space, Table, Tag, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import axios from '../../utilities/axios';

const { Text } = Typography;

const fetchMarginRequirements = (token) =>
    axios
        .get('/crud/margin-requirements', { headers: { token } })
        .then(({ data }) => (Array.isArray(data) ? data : []));

const normalizeTicker = (ticker) =>
    String(ticker || '')
        .trim()
        .toUpperCase();

const formatPercent = (value) =>
    `${Number(value || 0)
        .toFixed(2)
        .replace(/\.00$/, '')}%`;

const formatActor = (actor) => {
    if (!actor) return 'Unknown';

    const name = [actor.firstName, actor.lastName].filter(Boolean).join(' ').trim();
    return name || actor.email || 'Unknown';
};

const MarginRequirementsPanel = ({ token }) => {
    const queryClient = useQueryClient();
    const [ticker, setTicker] = useState('');
    const [requirementPercent, setRequirementPercent] = useState(50);

    const queryKey = ['margin-requirements'];
    const { data = [], isLoading } = useQuery(queryKey, () => fetchMarginRequirements(token), {
        enabled: Boolean(token)
    });

    const saveMutation = useMutation(
        (payload) =>
            axios.post('/crud/margin-requirements', payload, {
                headers: { token }
            }),
        {
            onSuccess: () => {
                message.success('Margin requirement saved');
                setTicker('');
                queryClient.invalidateQueries(queryKey);
            },
            onError: (error) => {
                message.error(error?.response?.data?.error || 'Failed to save margin requirement');
            }
        }
    );

    const deleteMutation = useMutation(
        (rowTicker) =>
            axios.delete(`/crud/margin-requirements/${encodeURIComponent(rowTicker)}`, {
                headers: { token }
            }),
        {
            onSuccess: () => {
                message.success('Margin requirement removed');
                queryClient.invalidateQueries(queryKey);
            },
            onError: (error) => {
                message.error(
                    error?.response?.data?.error || 'Failed to remove margin requirement'
                );
            }
        }
    );

    const rows = useMemo(
        () =>
            data.map((row) => ({
                key: row._id || row.ticker,
                ...row
            })),
        [data]
    );

    const handleSave = () => {
        const normalizedTicker = normalizeTicker(ticker);
        if (!normalizedTicker) {
            message.error('Ticker is required');
            return;
        }

        saveMutation.mutate({
            ticker: normalizedTicker,
            requirementPercent: Number(requirementPercent)
        });
    };

    const columns = [
        {
            title: 'Ticker',
            dataIndex: 'ticker',
            width: 140,
            render: (value) => <Tag color="blue">{value}</Tag>
        },
        {
            title: 'Margin Requirement',
            dataIndex: 'requirementPercent',
            render: (value) => <Text strong>{formatPercent(value)}</Text>
        },
        {
            title: 'Buying Power Charge',
            dataIndex: 'requirementPercent',
            render: (value) => `${Math.max(Number(value) / 25, 1).toFixed(2)}x normal`
        },
        {
            title: 'Added By',
            dataIndex: 'addedBy',
            render: (value) => formatActor(value)
        },
        {
            title: '',
            width: 80,
            render: (_, record) => (
                <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleteMutation.isLoading && deleteMutation.variables === record.ticker}
                    onClick={() => deleteMutation.mutate(record.ticker)}
                    size="small"
                />
            )
        }
    ];

    return (
        <Card className="admin-dashboard-margin-card" size="small">
            <div className="admin-dashboard-margin-card__header">
                <div className="admin-dashboard-margin-card__title-group">
                    <Text className="admin-dashboard-margin-card__title" strong>
                        Ticker Margin Requirements
                    </Text>
                    <Text className="admin-dashboard-margin-card__description" type="secondary">
                        Default 4x leverage is 25%. Higher ticker requirements consume more buying
                        power.
                    </Text>
                </div>
                <Space className="admin-dashboard-margin-card__controls" wrap>
                    <Input
                        onChange={(event) => setTicker(normalizeTicker(event.target.value))}
                        placeholder="Ticker"
                        style={{ width: 120 }}
                        value={ticker}
                    />
                    <InputNumber
                        addonAfter="%"
                        max={100}
                        min={1}
                        onChange={(value) => setRequirementPercent(value)}
                        precision={2}
                        style={{ width: 140 }}
                        value={requirementPercent}
                    />
                    <Button
                        icon={<SaveOutlined />}
                        loading={saveMutation.isLoading}
                        onClick={handleSave}
                        type="primary"
                    >
                        Save
                    </Button>
                </Space>
            </div>
            <Table
                columns={columns}
                dataSource={rows}
                loading={isLoading}
                pagination={false}
                size="small"
            />
        </Card>
    );
};

MarginRequirementsPanel.propTypes = {
    token: PropTypes.string
};

MarginRequirementsPanel.defaultProps = {
    token: null
};

export default MarginRequirementsPanel;
